import BaseApi, { Response, PagedResponse } from './baseApi';
import {
  BasePersonHealthProfile,
  CreatePersonHealthProfile,
  UpdatePersonHealthProfile,
} from '../dtos/PersonHealthProfiles/index';

class PersonHealthProfileApi extends BaseApi {
  private readonly basePath = '/person-health-profiles';

  /**
   * Get person health profile by ID
   * GET /api/person-health-profiles/{id}
   */
  async getById(id: string): Promise<Response<BasePersonHealthProfile | null>> {
    return this.get<BasePersonHealthProfile | null>(`${this.basePath}/${id}`);
  }

  /**
   * Get person health profile by person ID
   * GET /api/person-health-profiles/person/{personId}
   */
  async getByPersonId(personId: string): Promise<Response<BasePersonHealthProfile | null>> {
    return this.get<BasePersonHealthProfile | null>(`${this.basePath}/person/${personId}`);
  }

  /**
   * Get all person health profiles
   * GET /api/person-health-profiles
   */
  async getList(): Promise<Response<BasePersonHealthProfile[]>> {
    return this.get<BasePersonHealthProfile[]>(this.basePath);
  }

  /**
   * Get paginated list of person health profiles with optional search
   * Always uses /search endpoint to get person details (name, internalId)
   * GET /api/person-health-profiles/search?searchTerm=&pageNumber=1&pageSize=10
   * Searches: PersonId (as string) + keyword searches for conditions (smoker, diabetes, etc.)
   */
  async getPagedList(
    pageNumber: number = 1,
    pageSize: number = 10,
    searchTerm?: string
  ): Promise<Response<PagedResponse<BasePersonHealthProfile>>> {
    // Always use /search endpoint to get person details included
    const params = new URLSearchParams();
    params.append('searchTerm', searchTerm || '');
    params.append('pageNumber', pageNumber.toString());
    params.append('pageSize', pageSize.toString());
    return this.get<PagedResponse<BasePersonHealthProfile>>(
      `${this.basePath}/search?${params.toString()}`
    );
  }

  /**
   * Create a new person health profile
   * POST /api/person-health-profiles
   */
  async create(data: CreatePersonHealthProfile): Promise<Response<BasePersonHealthProfile>> {
    return this.post<BasePersonHealthProfile, CreatePersonHealthProfile>(this.basePath, data);
  }

  /**
   * Update an existing person health profile
   * PUT /api/person-health-profiles/{id}
   */
  async update(id: string, data: Omit<UpdatePersonHealthProfile, 'id'>): Promise<Response<BasePersonHealthProfile>> {
    return this.put<BasePersonHealthProfile, UpdatePersonHealthProfile>(`${this.basePath}/${id}`, { ...data, id });
  }

  /**
   * Delete a person health profile
   * DELETE /api/person-health-profiles/{id}
   */
  async deletePersonHealthProfile(id: string): Promise<Response<boolean>> {
    return super.delete<boolean>(`${this.basePath}/${id}`);
  }
}

// Export singleton instance
export const personHealthProfileApi = new PersonHealthProfileApi();

export default PersonHealthProfileApi;
